/*
 * What the recruiter reads when something goes wrong.
 *
 * Errors reach the screen as many shapes — an ApolloError, a GraphQL error
 * object, a network failure, a plain Error, or the raw `error.message` string a
 * screen pulled off one — and too often the raw server text went straight into a
 * toast ("Something error, cannot pull data from superset", "GraphQL error: …",
 * "Network error: Failed to fetch"). This is the one place that turns any of them
 * into a sentence that says what happened and what to do next.
 *
 * Rules, in order:
 * 1. Known codes (permission, not found, rate limit, server failure, offline) get
 *    our own copy, whatever the server said.
 * 2. Validation and business refusals keep the server's sentence: that text is
 *    written for the recruiter and names the field ("Name is required").
 * 3. Anything else keeps the server's sentence only when it reads like one; a
 *    technical message is replaced by the caller's fallback, or a generic line.
 */

export const USER_MESSAGES = {
	generic: 'Something went wrong. Please try again.',
	server: 'Something went wrong on our side. Please try again in a moment.',
	offline: 'You’re offline. Check your connection and try again.',
	unreachable: 'We couldn’t reach Maestro. Check your connection and try again.',
	timeout: 'This is taking longer than expected. Please try again.',
	forbidden: 'You don’t have permission to do this. Ask your Maestro admin if you need access.',
	notFound: 'This item no longer exists. Refresh the page to see the latest.',
	rateLimited: 'Too many attempts in a short time. Wait a moment, then try again.',
	sessionExpired: 'Your session has expired. Please sign in again.',
	loadFailed: 'Some information couldn’t be loaded. Refresh the page to try again.',
	// A refusal that a reload would only repeat: no promise that refreshing helps.
	loadRefused: 'Some information on this page couldn’t be loaded. If it keeps happening, let your Maestro admin know.',
}

const FORBIDDEN_CODES = ['FORBIDDEN', 'UNAUTHORIZED', 'PERMISSION_DENIED', 'ACCESS_DENIED', 'NOT_AUTHORIZED', 403]
const NOT_FOUND_CODES = ['NOT_FOUND', 'RECORD_NOT_FOUND', 404]
const RATE_LIMIT_CODES = ['RATE_LIMITED', 'TOO_MANY_REQUESTS', 429]
const SERVER_CODES = ['INTERNAL_SERVER_ERROR', 'SERVER_ERROR', 500, 502, 503, 504]
// The server's own sentence is the right one for these: it was written for the person filling the form.
const PASS_THROUGH_CODES = ['VALIDATION_ERROR', 'REASON_REQUIRED', 'UNPROCESSABLE_ENTITY', 'BAD_USER_INPUT', 'EMPLOYER_ALREADY_EXISTS', 'AI_ASSIST_FAILED', 422]

// Wording of a permission refusal. Narrow on purpose: a sentence that merely mentions permissions is kept.
const FORBIDDEN_TEXT = /\b((do not|don't|not) have (the )?permission|not authori[sz]ed|forbidden|access denied|permission denied)\b/i

/*
 * Signs that a message was written for a developer, not a recruiter. Kept narrow
 * on purpose: a false positive only swaps the server's sentence for the fallback.
 */
const TECHNICAL_TEXT = [
	/^\s*(GraphQL|Network) error\b/i,
	/\b(exception|stack ?trace|undefined method|nil:NilClass|NoMethodError|ActiveRecord|PG::|SQL|syntax error)\b/i,
	/\b(Cannot (read|query)|is not a function|Unexpected token|Variable "\$|Field '|doesn't exist on type)\b/i,
	/\b(Failed to fetch|NetworkError|ECONNREFUSED|ETIMEDOUT|timeout of \d+ms)\b/i,
	/\bSomething error\b/i,
	/\b(Internal server error|status code \d{3})\b/i,
	/[{}<>[\]]{2,}|=>|::/,
]

const STRIP_PREFIX = /^\s*(GraphQL error|Network error|Error):\s*/i

const toText = (value) => (typeof value === 'string' ? value.replace(STRIP_PREFIX, '').trim() : '')

/** A sentence a recruiter can read: short, not a code dump, not empty. */
export const isReadableMessage = (message) => {
	const text = typeof message === 'string' ? message.trim() : ''
	if (text.length < 3 || text.length > 240) return false
	return !TECHNICAL_TEXT.some((pattern) => pattern.test(text))
}

const firstGraphQLError = (error) => {
	if (!error || typeof error !== 'object') return null
	if (Array.isArray(error.graphQLErrors) && error.graphQLErrors.length) return error.graphQLErrors[0]
	if (Array.isArray(error.errors) && error.errors.length) return error.errors[0]
	// A single GraphQL error object, as the error link hands it over.
	if (error.extensions || (error.message && error.path)) return error
	return null
}

/** The status or code an error carries, from any of the shapes above. */
export const errorCode = (error) => {
	const gql = firstGraphQLError(error)
	const code = gql?.extensions?.code ?? error?.extensions?.code
	if (code !== undefined && code !== null) return code
	return error?.networkError?.statusCode ?? error?.statusCode ?? error?.response?.status ?? null
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false

/** A failure to reach the server at all, rather than a refusal from it. */
export const isNetworkError = (error) => {
	if (!error || typeof error !== 'object') return false
	if (error.networkError && !firstGraphQLError(error)) return true
	if (error.name === 'TypeError' && /fetch/i.test(error.message || '')) return true
	return /^\s*Network error\b|Failed to fetch|NetworkError/i.test(error.message || '')
}

const includesCode = (codes, code) => codes.some((item) => String(item).toUpperCase() === String(code).toUpperCase())

/**
 * The sentence to show for `error`.
 *
 * @param error   anything a catch block or an error link can hold, or a message string
 * @param fallback what to say when the server's text is not fit to show; say what failed
 *                 ("Couldn't save the template.") so the recruiter knows which action it was
 */
export const toUserMessage = (error, fallback) => {
	const fallbackText = fallback || USER_MESSAGES.generic

	if (error === null || error === undefined || error === '') return fallbackText

	if (typeof error === 'string') {
		const text = toText(error)
		if (/Failed to fetch|NetworkError|^\s*Network error/i.test(error)) return isOffline() ? USER_MESSAGES.offline : USER_MESSAGES.unreachable
		if (FORBIDDEN_TEXT.test(text)) return USER_MESSAGES.forbidden
		return isReadableMessage(text) ? text : fallbackText
	}

	if (isNetworkError(error)) {
		// A status means the server answered: say what it answered. No status means it was never reached.
		const status = error?.networkError?.statusCode || error?.statusCode
		if (status === 408 || /timeout/i.test(error?.networkError?.message || '')) return USER_MESSAGES.timeout
		if (status && includesCode(SERVER_CODES, status)) return USER_MESSAGES.server
		if (status && includesCode(FORBIDDEN_CODES, status)) return USER_MESSAGES.forbidden
		if (status && includesCode(NOT_FOUND_CODES, status)) return USER_MESSAGES.notFound
		if (status && includesCode(RATE_LIMIT_CODES, status)) return USER_MESSAGES.rateLimited
		if (status) return fallbackText
		return isOffline() ? USER_MESSAGES.offline : USER_MESSAGES.unreachable
	}

	const gql = firstGraphQLError(error)
	const code = errorCode(error)
	const serverText = toText(gql?.message || error?.message)

	if (code !== null && includesCode(PASS_THROUGH_CODES, code)) return isReadableMessage(serverText) ? serverText : fallbackText
	if (code !== null && includesCode(FORBIDDEN_CODES, code)) return USER_MESSAGES.forbidden
	if (code !== null && includesCode(NOT_FOUND_CODES, code)) return USER_MESSAGES.notFound
	if (code !== null && includesCode(RATE_LIMIT_CODES, code)) return USER_MESSAGES.rateLimited
	if (code !== null && includesCode(SERVER_CODES, code)) return USER_MESSAGES.server
	if (FORBIDDEN_TEXT.test(serverText)) return USER_MESSAGES.forbidden

	return isReadableMessage(serverText) ? serverText : fallbackText
}

/**
 * A failure that may pass on its own (the connection dropped, the server was down
 * or busy, the request timed out), so "refresh and try again" is honest advice.
 * A GraphQL refusal the server answered on purpose is not: reloading only asks
 * the same question and gets the same answer.
 */
export const isTransientError = (error) => {
	if (isNetworkError(error)) {
		const status = error?.networkError?.statusCode || error?.statusCode
		return !status || status === 408 || includesCode(SERVER_CODES, status) || includesCode(RATE_LIMIT_CODES, status)
	}
	const code = errorCode(error)
	return code !== null && (includesCode(SERVER_CODES, code) || includesCode(RATE_LIMIT_CODES, code))
}

/** A permission refusal, which a screen should explain by hiding the control rather than with a toast. */
export const isForbiddenError = (error) => {
	const code = errorCode(error)
	if (code !== null && includesCode(FORBIDDEN_CODES, code)) return true
	const gql = firstGraphQLError(error)
	return FORBIDDEN_TEXT.test(toText(gql?.message || error?.message || (typeof error === 'string' ? error : '')))
}

/*
 * One failure, one message. The error link tells the recruiter about a failed
 * mutation; the screen's own `catch` then often calls `notifyStore.error(error)`
 * with the very same error. Marking what was already shown lets the second call
 * stand down instead of stacking a duplicate.
 */
const notified = new WeakSet()

const parts = (error) => {
	if (!error || typeof error !== 'object') return []
	return [error, error.networkError, ...(Array.isArray(error.graphQLErrors) ? error.graphQLErrors : [])].filter((part) => part && typeof part === 'object')
}

export const markNotified = (error) => parts(error).forEach((part) => notified.add(part))

export const wasNotified = (error) => parts(error).some((part) => notified.has(part))

/**
 * For a screen that shows a failure in place (an error state with Retry): tells
 * the API layer it was handled, so no toast is added on top of it.
 */
export const markHandled = markNotified

/**
 * A query answered with errors and nothing to show is a failure, even under the
 * app's `errorPolicy: 'all'`, which resolves instead of rejecting. Returns an
 * Error carrying those GraphQL errors (so `markHandled` reaches the ones the API
 * layer saw), or null when there is data to show.
 */
export const failureOf = (response, hasData = (data) => Boolean(data)) => {
	if (!response?.errors?.length || hasData(response.data)) return null
	const failure = new Error(response.errors[0]?.message || 'Request failed')
	failure.graphQLErrors = response.errors
	return failure
}
